import React from 'react';

import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { Box, Flex, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import messages from './messages';

/**
 * Number of placeholder rows shown while recent work loads. Kept modest so the skeleton
 * hints at the list shape without implying an exact result count.
 */
const SKELETON_ROW_COUNT = 4;

const styles = cssMap({
	// Mirrors `RecentWorkResults` `resultsRoot` so the skeleton and the real list occupy
	// the same footprint (no layout shift when results replace it).
	root: {
		width: '100%',
	},
	// Section-header placeholder, sized to roughly match the "Recents" heading line.
	headerLine: {
		width: '80px',
		height: '12px',
		marginTop: token('space.200'),
		marginBottom: token('space.050'),
		borderRadius: token('radius.small'),
		backgroundColor: token('color.skeleton'),
	},
	// Matches `resultsContainer` (rounded surface card) so rows clip identically.
	container: {
		width: '100%',
		backgroundColor: token('elevation.surface'),
		borderRadius: token('radius.large'),
		overflow: 'hidden',
		alignItems: 'stretch',
	},
	// Matches `resultRow` padding so each skeleton row lines up with a real row.
	row: {
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
		paddingRight: token('space.100'),
	},
	// Matches `iconBadge` (32x32 rounded) so the leading glyph placeholder aligns.
	iconBadge: {
		width: '32px',
		height: '32px',
		flexShrink: 0,
		borderRadius: token('radius.medium'),
		backgroundColor: token('color.skeleton'),
	},
	textColumn: {
		minWidth: '0px',
		flex: '1',
	},
	titleLine: {
		width: '70%',
		height: '12px',
		borderRadius: token('radius.small'),
		backgroundColor: token('color.skeleton'),
	},
	subtitleLine: {
		width: '40%',
		height: '10px',
		borderRadius: token('radius.small'),
		backgroundColor: token('color.skeleton'),
	},
});

const SkeletonRow = (): React.JSX.Element => (
	<Flex xcss={styles.row} alignItems="center" gap="space.100">
		<Box xcss={styles.iconBadge} />
		<Stack xcss={styles.textColumn} space="space.075">
			<Box xcss={styles.titleLine} />
			<Box xcss={styles.subtitleLine} />
		</Stack>
	</Flex>
);

/**
 * Placeholder shown while the recent-work pool is loading. Mirrors the {@link RecentWorkResults}
 * layout (section header + rounded card of rows) so the transition to real results is pop-free.
 */
export const LoadingSkeleton = (): React.JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Stack
			xcss={styles.root}
			space="space.050"
			role="status"
			aria-label={formatMessage(messages.recentWorkLoadingLabel)}
			testId="recent-work-results-skeleton"
		>
			<Box xcss={styles.headerLine} />
			<Stack xcss={styles.container}>
				{Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => (
					<SkeletonRow key={index} />
				))}
			</Stack>
		</Stack>
	);
};
